import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X, GripVertical, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';


const AdminScorecardScorecardPage = () => {
    const [questions, setQuestions] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, optionId: null });

    const [editingOption, setEditingOption] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [questionsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/eligibility-scorecard/questions?page_type=scorecard_page'),
                axios.get('/api/admin/eligibility-scorecard/settings?page_type=scorecard_page')
            ]);

            if (questionsRes.data.success) {
                setQuestions(questionsRes.data.data);
            }
            if (settingsRes.data.success) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching scorecard data:', error);
            toast.error('Failed to load scorecard data');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateQuestion = async (questionId, updates) => {
        try {
            const response = await axios.put(`/api/admin/eligibility-scorecard/questions/${questionId}`, { ...updates, page_type: 'scorecard_page' });
            if (response.data.success) {
                toast.success('Question updated successfully');
                fetchData();
                setEditingQuestion(null);
            }
        } catch (error) {
            console.error('Error updating question:', error);
            toast.error('Failed to update question');
        }
    };

    const handleUpdateOption = async (optionId, updates) => {
        try {
            const response = await axios.put(`/api/admin/eligibility-scorecard/options/${optionId}`, updates);
            if (response.data.success) {
                toast.success('Option updated successfully');
                fetchData();
                setEditingOption(null);
            }
        } catch (error) {
            console.error('Error updating option:', error);
            toast.error('Failed to update option');
        }
    };

    const handleToggleActive = async (questionId, currentStatus) => {
        try {
            const question = questions.find(q => q.id === questionId);
            await handleUpdateQuestion(questionId, {
                ...question,
                is_active: !currentStatus
            });
        } catch (error) {
            console.error('Error toggling question status:', error);
        }
    };

    const handleDeleteOption = (optionId) => {
        setDeleteConfirm({ isOpen: true, optionId });
    };

    const confirmDeleteOption = async () => {
        try {
            const response = await axios.delete(`/api/admin/eligibility-scorecard/options/${deleteConfirm.optionId}`);
            if (response.data.success) {
                toast.success('Option deleted successfully');
                fetchData();
            }
        } catch (error) {
            console.error('Error deleting option:', error);
            toast.error('Failed to delete option');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#283079]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Eligibility Scorecard</h1>
                    <p className="text-slate-500 mt-1">Manage questions and options for the eligibility scorecard</p>
                </div>
                <button
                    onClick={fetchData}
                    className="px-4 py-2 bg-[#283079] text-white rounded-lg hover:bg-[#1f2559] transition-colors"
                >
                    Refresh
                </button>
            </div>

            {/* Settings Card */}
            {settings && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-slate-800">Section Settings</h2>
                        {editingQuestion !== 'settings' && (
                            <button
                                onClick={() => setEditingQuestion('settings')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <Edit2 size={16} />
                                Edit Settings
                            </button>
                        )}
                    </div>

                    {editingQuestion === 'settings' ? (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Section Title</label>
                                <input
                                    type="text"
                                    defaultValue={settings.section_title}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                    id="settings-section-title"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Section Subtitle</label>
                                <textarea
                                    defaultValue={settings.section_subtitle}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                    rows={2}
                                    id="settings-section-subtitle"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Result Title</label>
                                <input
                                    type="text"
                                    defaultValue={settings.result_title}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                    id="settings-result-title"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Result Description</label>
                                <textarea
                                    defaultValue={settings.result_description}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                    rows={2}
                                    id="settings-result-description"
                                />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button
                                    onClick={async () => {
                                        const sectionTitle = document.getElementById('settings-section-title').value;
                                        const sectionSubtitle = document.getElementById('settings-section-subtitle').value;
                                        const resultTitle = document.getElementById('settings-result-title').value;
                                        const resultDescription = document.getElementById('settings-result-description').value;

                                        try {
                                            const response = await axios.put('/api/admin/eligibility-scorecard/settings', {
                                                section_title: sectionTitle,
                                                section_subtitle: sectionSubtitle,
                                                hero_title: settings.hero_title,
                                                hero_description: settings.hero_description,
                                                result_title: resultTitle,
                                                result_description: resultDescription,
                                                page_type: 'scorecard_page'
                                            });

                                            if (response.data.success) {
                                                toast.success('Settings updated successfully');
                                                fetchData();
                                                setEditingQuestion(null);
                                            }
                                        } catch (error) {
                                            console.error('Error updating settings:', error);
                                            toast.error('Failed to update settings');
                                        }
                                    }}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                >
                                    <Save size={16} />
                                    Save Settings
                                </button>
                                <button
                                    onClick={() => setEditingQuestion(null)}
                                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors flex items-center gap-2"
                                >
                                    <X size={16} />
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Section Title</label>
                                <p className="text-slate-900">{settings.section_title}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Section Subtitle</label>
                                <p className="text-slate-900">{settings.section_subtitle}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Result Title</label>
                                <p className="text-slate-900">{settings.result_title}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Result Description</label>
                                <p className="text-slate-900">{settings.result_description}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Questions List */}
            <div className="space-y-4">
                {questions.map((question, qIndex) => (
                    <div
                        key={question.id}
                        className={`bg-white rounded-xl border-2 ${question.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50/30'
                            } overflow-hidden`}
                    >
                        {/* Question Header */}
                        <div className="p-6 border-b border-slate-100">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#283079] text-white font-bold text-sm">
                                            {qIndex + 1}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${question.question_type === 'text_input'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-green-100 text-green-700'
                                            }`}>
                                            {question.question_type === 'text_input' ? 'Text Input' : 'Multiple Choice'}
                                        </span>
                                        {question.section_category && (
                                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                                                {question.section_category}
                                            </span>
                                        )}
                                    </div>

                                    {editingQuestion === question.id ? (
                                        <div className="space-y-3">
                                            <div>
                                                <textarea
                                                    defaultValue={question.question_text}
                                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                                    rows={2}
                                                    id={`question-text-${question.id}`}
                                                    maxLength={130}
                                                    onChange={(e) => {
                                                        const counter = document.getElementById(`question-char-count-${question.id}`);
                                                        if (counter) counter.textContent = e.target.value.length;
                                                    }}
                                                />
                                                <div className="flex justify-end mt-1">
                                                    <span className="text-xs text-slate-500">
                                                        <span id={`question-char-count-${question.id}`}>{question.question_text.length}</span>/130 characters
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        const newText = document.getElementById(`question-text-${question.id}`).value;
                                                        if (newText.length > 130) {
                                                            toast.error('Question must be 130 characters or less');
                                                            return;
                                                        }
                                                        handleUpdateQuestion(question.id, {
                                                            ...question,
                                                            question_text: newText
                                                        });
                                                    }}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                                >
                                                    <Save size={16} />
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setEditingQuestion(null)}
                                                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors flex items-center gap-2"
                                                >
                                                    <X size={16} />
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-lg font-semibold text-slate-800">{question.question_text}</p>
                                    )}
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setEditingQuestion(editingQuestion === question.id ? null : question.id)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Edit Question"
                                    >
                                        <Edit2 size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleToggleActive(question.id, question.is_active)}
                                        className={`p-2 rounded-lg transition-colors ${question.is_active
                                            ? 'text-green-600 hover:bg-green-50'
                                            : 'text-red-600 hover:bg-red-50'
                                            }`}
                                        title={question.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                        {question.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Options List */}
                        {question.options && question.options.length > 0 && (
                            <div className="p-6">
                                <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">Options</h3>
                                <div className="space-y-2">
                                    {question.options.map((option, oIndex) => (
                                        <div
                                            key={option.id}
                                            className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                                        >
                                            <span className="text-slate-400 font-bold text-sm">{oIndex + 1}.</span>

                                            {editingOption === option.id ? (
                                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                                    <div>
                                                        <input
                                                            type="text"
                                                            defaultValue={option.option_label}
                                                            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent w-full"
                                                            placeholder="Option Label"
                                                            id={`option-label-${option.id}`}
                                                            maxLength={75}
                                                            onChange={(e) => {
                                                                const counter = document.getElementById(`option-char-count-${option.id}`);
                                                                if (counter) counter.textContent = e.target.value.length;
                                                            }}
                                                        />
                                                        <div className="flex justify-end mt-1">
                                                            <span className="text-xs text-slate-500">
                                                                <span id={`option-char-count-${option.id}`}>{option.option_label.length}</span>/75 characters
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        defaultValue={option.option_value}
                                                        className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                                        placeholder="Option Value"
                                                        id={`option-value-${option.id}`}
                                                        maxLength={75}
                                                    />
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        defaultValue={option.points}
                                                        className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                                        placeholder="Points"
                                                        id={`option-points-${option.id}`}
                                                    />
                                                    <div className="flex gap-2 md:col-span-3">
                                                        <button
                                                            onClick={() => {
                                                                const newLabel = document.getElementById(`option-label-${option.id}`).value;
                                                                const newValue = document.getElementById(`option-value-${option.id}`).value;
                                                                const newPoints = parseFloat(document.getElementById(`option-points-${option.id}`).value);

                                                                if (newLabel.length > 75) {
                                                                    toast.error('Option label must be 75 characters or less');
                                                                    return;
                                                                }
                                                                if (newValue.length > 75) {
                                                                    toast.error('Option value must be 75 characters or less');
                                                                    return;
                                                                }

                                                                handleUpdateOption(option.id, {
                                                                    option_label: newLabel,
                                                                    option_value: newValue,
                                                                    points: newPoints,
                                                                    display_order: option.display_order
                                                                });
                                                            }}
                                                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                                                        >
                                                            <Save size={14} />
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingOption(null)}
                                                            className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm flex items-center gap-1"
                                                        >
                                                            <X size={14} />
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex-1">
                                                        <p className="font-medium text-slate-800">{option.option_label}</p>
                                                        <p className="text-sm text-slate-500">{option.option_value}</p>
                                                    </div>
                                                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${option.points >= 0
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                        }`}>
                                                        {option.points >= 0 ? '+' : ''}{option.points} pts
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => setEditingOption(option.id)}
                                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                            title="Edit Option"
                                                        >
                                                            <Edit2 size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteOption(option.id)}
                                                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="Delete Option"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {questions.length === 0 && (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <p className="text-slate-500">No questions found</p>
                </div>
            )}

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, optionId: null })}
                onConfirm={confirmDeleteOption}
                title="Delete Option"
                message="Are you sure you want to delete this option? This action cannot be undone."
            />
        </div>
    );
};

export default AdminScorecardScorecardPage;
